// ** React Imports
import { React, Fragment, useEffect, useState, useRef } from "react";
import { Link, useParams, useNavigate, useLocation  } from "react-router-dom";

// ** MUI Imports
import {
  Avatar,
  AvatarGroup,
  Stepper,
  StepLabel,
  IconButton,
  Button,
  TextField,
  Tabs,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Menu,
  MenuItem,
  FormControl,
  FormLabel,
  FormControlLabel,
  FormGroup,
  Grid,
  Divider,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  CircularProgress,
  Backdrop,
  Popover,
} from "@mui/material";
import MuiStep from "@mui/material/Step";
import { styled, useTheme } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import Tab from "@mui/material/Tab";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import TabContext from "@mui/lab/TabContext";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineContent from "@mui/lab/TimelineContent";
import useMediaQuery from "@mui/material/useMediaQuery";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import MuiTimeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DataGrid } from "@mui/x-data-grid";
import PropertyAreaTab from "./component/PropertyAreaTab";
import { GetEntityLinks, DeleteLinkRecord } from "screens/Requests/logic/LinkRecordLogic";
// ** Util Imports
import { hexToRGBA } from "../../components/utils/hex-to-rgba";

// ** Custom Component Imports
import CustomTextField from "../../components/mui/text-field";
import CustomChip from "../../components/mui/chip";

// ** Component Imports
// import AddJobsiteDialog from "../Customers/component/AddJobsite";
// import AddContactDialog from "../Customers/component/AddContact";
// import AddServiceDialog from "./component/AddService";
import DateRangeCalendar from "../Customers/component/DateRangeCalendar";
// import SearchRecordsDialog from "./component/SearchRecords";
// import AttachmentUploadDialog from "components/AttachmentUpload";
import WorkRequirements from "./component/WorkRequirements";
import AddServiceDialog from "./component/AddService";
import ScheduleAssessment from "screens/Requests/component/ScheduleAssessment";
import AddCommunicationLogDialog from "screens/Customers/component/AddCommunicationLog";
import LinkedRecordsComponent from "screens/Requests/component/LinkedRecordsComponent";
import NotesComponent from "screens/Requests/component/NotesComponent";
import AssignToComponent from "screens/Assessments/component/AssignToComponent";
import ConversionComponent from "screens/Requests/component/ConversionComponent";
import AddBundle from "./component/AddBundle";
import AddService from "./component/AddService";
import UnsavedChangesDialog from "components/UnsavedChangesDialog";
import GenerateInvoiceDialog from './component/GenerateInvoice';

// ** Icon Imports
import Icon from "../../components/icon";
import InfoIcon from "@mui/icons-material/Info";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

// ** Custom Components Imports
import StepperCustomDot from "../../components/Stepper/StepperCustomDot";
import CustomAvatar from "../../components/mui/avatar";
import CustomAutocomplete from "components/mui/autocomplete";
import OptionsMenu from "../../components/option-menu";

// ** Styled Component
import StepperWrapper from "../../components/styles/mui/stepper";

// ** Third Party Components
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDropzone } from "react-dropzone";
import dayjs from "dayjs";
import { format } from "date-fns";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";

// ** Layout Import
import UserLayout from "../../components/layouts/UserLayout";
import AddCustomerDialog from "screens/Customers/component/AddCustomer";
import { useIndexLogic } from "screens/Customers/logic/indexLogic";
import { useCustomerLogic } from "screens/Customers/logic/CustomerLogic";
import { useCreateAssessmentLogic } from "./logic/create-assessment-logic";
import { GetServiceRequestRaw, GetServiceRequests } from "screens/Requests/logic/GetServiceRequest";
import { GetJobsite } from "screens/Customers/logic/GetJobsites";
import AddContactDialog from "screens/Customers/component/AddContact";
import TagManagementDialog from "screens/Customers/component/TagManagement";
import _ from 'lodash';
import { getStatusRules, STATUS_ORDER, STATUS_RULES, canTransition} from "./logic/StatusManager";
import { useUnsavedChanges } from "components/UnsavedChangesContext";
import { GetSchedule } from "../Requests/logic/SchedulingLogic";
import { useWorkRequirementLogic } from "screens/WorkOrders/logic/work-requirement-logic";
import PreAttachment from "screens/Requests/component/PreAttachment";
import { useAssignToLogic } from "./logic/AssignToLogic";
import { useAssessmentPermission } from "./permissions/AssessmentPermissions";
import AttachmentsComponent from "screens/Requests/component/AttachmentsComponent";
import { SendNotification } from "components/SendNotification";
import AddEmployeeDoodle from "images/AddEmployeeDoodle.png";
import AddJobsiteDialog from "screens/Customers/component/AddJobsite";

//#region Constants

const stepsRequestDetails = [
  {
    icon: "tabler:user",
    title: "Basic Information",
  },
  {
    icon: "tabler:address-book",
    title: "Work Requirements",
  },
];

// Styled Timeline component
const Timeline = styled(MuiTimeline)({
  "& .MuiTimelineItem-root": {
    width: "100%",
    "&:before": {
      display: "none",
    },
  },
});

// ** Styled Components
const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : theme.palette.common.white,
    minWidth: 220,
    maxWidth: 440,
    border: "1px solid #E1E4EA",
  },
}));


const Customersteps = [
  {
    icon: "tabler:user",
    title: "Profile Information",
  },
  {
    icon: "tabler:address-book",
    title: "Add Contact",
  },
  {
    icon: "tabler:align-box-bottom-center",
    title: "Add Jobsite",
  },
];


const parseAvailabilityDays = (daysString) => {
  try {
    const daysArray = JSON.parse(daysString);
    // Ensure that only numbers are kept in the array
    return daysArray.filter((item) => typeof item === 'number');
  } catch (error) {
    //console.error('Error parsing availabilityDays:', error);
    return [];
  }
};

//#endregion Constants

const dropdownItemsStyle = {
  px: 6, 
  py: 2, 
  '&:hover': { 
    backgroundColor: 'action.hover', cursor: 'pointer' 
  }
}

const OptionsButton = ({generateInvoice}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <div>
      <IconButton onClick={handleClick}>
        <Icon icon='tabler:dots-vertical' />
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box sx={{ py: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography variant="h6" sx={dropdownItemsStyle} onClick={generateInvoice}>
              Charge for Assessment
            </Typography>
          </Box>
        </Box>
      </Popover>
    </div>
  );
};
  const CustomChipWithDropdown = ({ tag, onSelect }) => {
      const [anchorEl, setAnchorEl] = useState(null);
      const theme = useTheme();

      const isUnclickable = ["Estimate", "Work Order"].includes(tag);

      const handleClick = (event) => {
        if (isUnclickable) return;
        setAnchorEl(event.currentTarget);
      };

      const handleClose = () => {
        setAnchorEl(null);
      };

      const handleSelect = (selectedTag) => {
        handleClose();
        onSelect(selectedTag);
      };

      const statusColors = {
        "Under Review": "info",
        "Submitted": "success",
        "Estimate": "success",
        "On-Site Scheduled": "success",
        "Phone Scheduled": "success",
        "Rescheduled": "success",
        "Completed": "success",
        "On Hold": "warning",
        "Rejected": "error",
        "Closed": "error",
      };

      const statuses = ["On Hold", "Completed", "Rejected"];

      return (
        <Box>
      <CustomChip
      borderRadius="4px"
      size="small"
      skin="light"
      onClick={handleClick}
      color={statusColors[tag] || "secondary"}
      sx={{
        lineHeight: 1,
        py: 1,

        ...(isUnclickable && {
          pointerEvents: "none", 
          cursor: "default",
          "&:hover": {
            backgroundColor: "inherit !important",
            boxShadow: "none !important",
          },
          opacity: 1,
        }),
      }}

      label={
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <FiberManualRecordIcon fontSize="small" />
          <Typography variant="caption" sx={{ fontSize: "0.8rem" }}>
            Status:
          </Typography>
          <Typography variant="caption" sx={{ fontSize: "0.8rem", fontWeight: 660 }}>
            {tag}
          </Typography>
        </Box>
      }
    />
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            disableAutoFocusItem   
            disableRestoreFocus
            PaperProps={{ sx: { width: 200 } }}
          >
            {statuses.map((status) => {
              const isSelected = tag === status;

              return (
                <MenuItem
                  key={status}
                  selected={isSelected}
                  onClick={() => handleSelect(status)}
                  sx={{
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                    "&.Mui-selected": {
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.common.white,
                      "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                      },
                    },
                  }}
                >
                  {status}
                </MenuItem>
              );
            })}
          </Menu>
        </Box>
      );
    };

const AssessmentDetail = () => {
  //#region Permissions
  const { AssessmentPermissionProps } = useAssessmentPermission();
  const {
    hasAssessmentSavePermission,
  } = AssessmentPermissionProps;
  //#endregion Permissions

  // ** Hooks and Vars
  const MySwal = withReactContent(Swal);
  const navigate = useNavigate();
  const theme = useTheme();

  const { requestID } = useParams(); 

  //#region States
  const contentHeightFixed = false;
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState("1");
  // const [checked, setChecked] = useState([]);
  // const [openServiceDlg, setOpenServiceDlg] = useState(false);
  const [contactName, setContactName] = useState([]);
  //const [openService, setOpenService] = useState(false);
  const [sortTimeline, setSortTimeline] = useState(true);

  const [data, setData] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [jobsiteContacts, setJobsiteContacts] = useState([]);
  const [customerContacts, setCustomerContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState("Select");
  const [openScheduleAst, setOpenScheduleAst] = useState(false);
  const [schedulingReq, setSchedulingReq] = useState(false);
  const [openAssignTo, setOpenAssignTo] = useState(false);
  const [openUploadDlg, setOpenUploadDlg] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [openUploadDlg2, setOpenUploadDlg2] = useState(false);
  const [files, setFiles] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(new Date());
  const [openServiceDlg, setOpenServiceDlg] = useState(false);
  const [openComLog, setOpenComLog] = useState(false);
  const [logs, setLogs] = useState([]);
  const [openNewCustomerDlg, setOpenNewCustomerDlg] = useState(false);
  const {IndexProps} = useIndexLogic();
  const {fetchData, allCustomers} = IndexProps;
  const location = useLocation();
  const [tabValue, setTabValue] = useState("1");
  const [workRequirement, setWorkRequirement] = useState({});
  const [undoClicked, setUndoClicked] = useState(false);
  const [isScheduled, setIsScheduled] = useState(false);


  const {CustomerProps} = useCustomerLogic({activeStep, setActiveStep,Customersteps,setOpenNewCustomerDlg, fetchData, allCustomers});

  const {setCustomer, customer, allJobsites, setAllJobsites,setIsCompleted, isCompleted, fetchContacts, contacts,
    jobsite,
    setJobsite,
    openNewJobsiteDlg,
    setOpenNewJobsiteDlg,
    handleOpenNewJobsiteDlg,
    handleCloseNewJobsiteDlg,
    handleJobsiteChange,
    handleOpenTagManagementDlg,
    //handleDeleteTag,
    handleOpenNewCustomerDlg,
    openTagManagementDlg,
    handleCloseTagManagementDlg,        
  } = CustomerProps;

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { CreateAssessmentProps } = useCreateAssessmentLogic({handleClose,activeStep,setActiveStep,stepsRequestDetails,setLoading});
  
  const {
    isBundleEmpty,
    isServiceEmpty,
    //handleOpenBundleDlg,
    handleCloseBundleDlg,
    openAddBundle,
    openAddService,
    handleSave,
    handleFormat,
    handleAssessmentNext,
    handleBack,
    formats,
    ddlArray,
    allContacts,
    openNewContactDlg,
    handleCloseContactDialog,
    toast,                     
    handleAddTag,
    handleDeleteTag,
    serviceRequest,
    setServiceRequest,
    //  handleCloseNewJobsiteDlg,
    //  openNewJobsiteDlg,
    // setOpenNewJobsiteDlg,
    propertyArea,
    areaMeasurement,
    activeStepJobsite,
    jobsiteContent,
    clearPropertyArea,
    clearAreaMeasurement,
    setActiveStepJobsite,
    setPropertyArea,
    setAreaMeasurement,
    setAlertList,
    // handleJobsiteChange,
    alertList,
    NProgress,
    fetchJobsites,
    propertyAreaList,
    Tag,
    setTag,
    resetRequest,
    handleCustomerDropdownChange,
    requestErrors,
    handleJobsiteDropdownChange,
    handleContactDropdownChange,
    handleCompleteWorkByDateChange,
    SaveService,
    validateRequestField,
    // handleOpenNewJobsiteDlg,
    handleOpenNewContactDlg,
    //handleTimeChange, 
    fetchPropertyArea,
    setRequestErrors,
    workRequirementsList, 
    setWorkRequirementsList,
    workRequirementsListSub,
    setWorkRequirementsListSub,
    attachmentList, 
    setAttachmentList,
    //fetchWorkRequirements,   
  } = CreateAssessmentProps;

  const [assessmentID, setAssessmentID] = useState();
  const [activeSubTab, setActiveSubTab] = useState(0);

   const [initialServiceRequest, setInitialServiceRequest] = useState(serviceRequest);

   const prevRequesID = useRef();


  useEffect(() => {
    setAssessmentID(serviceRequest.requestID);
  }, [serviceRequest])

  const {  
    AssignToProps
  } = useAssignToLogic({serviceRequest, setLoading, requestID, requestType: "Assessment", navigate});

  const {
    filteredEmployees, 
    assignedUserIds, 
  } = AssignToProps;

  const [paginationModelComLog, setPaginationModelComLog] = useState({
    page: 0,
    pageSize: 5,
  });

  const [flagList, setFlagList] = useState({
    isCustomerLoaded:false, 
    isJobsiteLoaded:false, 
    isContactsLoaded:false, 
    isPrefferedTimeLoaded:false,
    isPreferedDaysLoaded:false,
    isCompleteWorkByLoaded:false, 
    isTagsLoaded:false,
    isViewed:false
  });

  const [status, setStatus] = useState('');
    const [rules, setRules] = useState(
      { 
        canEdit: true,
        canDelete: true,
        canSubmit: true,
        nextStatuses: [],
        cannotGoBack: false
      });
      const [scheduleStatus, setScheduleStatus] = useState('');
      // const [scheduleStatusBtn, setScheduleStatusBtn] = useState('');

   
  const handleServiceRequestChange = (key,value) => {
    setServiceRequest((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  }

  const [oldServiceRequest, setOldServiceRequest] = useState({});
 const [conversionBannerVisible, setConversionBannerVisible] = useState(false);
  const [linkedFromEntityId, setLinkedFromEntityId] = useState(null);
  const [isChanged, setIsChanged] = useState(false);
  const { setHasUnsavedChanges, hasUnsavedChanges } = useUnsavedChanges();
    const [openUnsavedChangesDlg, setOpenUnsavedChangesDlg] = useState(false);
 const [unsavedDialogAction, setUnsavedDialogAction] = useState({
    action: '',
    tabValue: '',
  });
   const handleOpenUnsavedChangesDlg = (action, tabValue) => {
    if(action === 'tabChange'){
      setUnsavedDialogAction({
        action: action,
        tabValue: tabValue,
      });
    }else{
      setUnsavedDialogAction({
        action: action,
        tabValue: '',
      });
    }
    setOpenUnsavedChangesDlg(true); 
  }
  const handleCloseUnsavedChangesDlg = () => setOpenUnsavedChangesDlg(false);
  const handleDiscardChanges = () => { 
    if(unsavedDialogAction.action === 'tabChange'){
      setOpenUnsavedChangesDlg(false); 
      setTabValue(unsavedDialogAction.tabValue)
    } else if(unsavedDialogAction.action === 'upload'){
      setOpenUnsavedChangesDlg(false); 
        setFiles([]);
        setUploadFiles([]);
        setOpenUploadDlg2(false); 
    }
    else{
      setOpenUnsavedChangesDlg(false); 
      navigate('/assessments')
    }
  }

  const [btnLoading, setButtonLoading] = useState(false);

  const handleNavigation = (to) => {   
    // if(isChanged){
     if(isChanged){
       handleOpenUnsavedChangesDlg('navigate');
      
      // MySwal.fire({
      //   title: "Are you sure?",
      //   html: "This page has unsaved changes",
    
      //   showCancelButton: true,
      //   confirmButtonColor: "#23A26D",
      //   cancelButtonColor: "#d33",
      //   confirmButtonText: "Discard Changes",
      //   cancelButtonText: "Cancel",
      //   allowEscapeKey: true,
      // }).then((result) => {
      //   if (result.isConfirmed) {
      //     navigate(-1);
      //     setHasUnsavedChanges(false);
      //   }
      // });
    }else{
      navigate(to);
    }
  };
  

  useEffect(() => {
    const isEqual = _.isEqual(oldServiceRequest, serviceRequest); // Deep compare OldEmp and employee
    setIsChanged(!isEqual); 
    setHasUnsavedChanges(!isEqual);
  }, [oldServiceRequest, serviceRequest]);

  
  useEffect(() => {
    // Check if we arrived from a conversion page using the flag in localStorage
    const conversionFlag = localStorage.getItem('fromConversion');

    if (conversionFlag === 'true') {
      // setIsFromConversion(true);
      setValue("7");
      
      // Optional: Clear the flag after checking (or leave it for future visits)
      localStorage.removeItem('fromConversion');
    }
  }, [location]); // Runs on location change

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
  
    const handleBackButton = (event) => {
      if (hasUnsavedChanges) {
        event.preventDefault();

         handleOpenUnsavedChangesDlg('navigate');
        // MySwal.fire({
        //   title: "Are you sure?",
        //   html: "This page has unsaved changes",
        
        //   showCancelButton: true,
        //   confirmButtonColor: "#23A26D",
        //   cancelButtonColor: "#d33",
        //   confirmButtonText: "Discard Changes",
        //   cancelButtonText: "Cancel",
        //   allowEscapeKey: true,
        // }).then((result) => {
        //   if (result.isConfirmed) {
        //     navigate(-1);
        //     setHasUnsavedChanges(false);
        //   }
        // });
      }else{
        navigate('/assessments');
      }
    };
  
    window.history.pushState(null, '', window.location.href);
  
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handleBackButton);
  
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handleBackButton);
    };
  }, [hasUnsavedChanges]);

  const availabilityDaysArray = parseAvailabilityDays(serviceRequest.availabilityDays || "[]");

  const handlePaginationModelComLog = (model) => {
    if (model.pageSize === -1) {
      setPaginationModelComLog({
        page: 0,
        pageSize: -1,
      });
    } else {
      setPaginationModelComLog(model);
    }
  };

  const handleOpenScheduleAstDlg = () => setOpenScheduleAst(true);
  const handleCloseScheduleAstDlg = () => setOpenScheduleAst(false);

  //const handleOpenServiceDlg = () => setOpenServiceDlg(true);
  const handleCloseServiceDlg = () => setOpenServiceDlg(false);

  const handleOpenUploadDlg = () => {
    setOpenUploadDlg(true);
  };

  const handleOpenNewComLogDlg = () => setOpenComLog(true);
  const handleCloseNewComLogDlg = () => setOpenComLog(false);


  /// Work Requirement
  
  const {
      AddServiceProps,
      AddBundleProps,
      WorkRequirementsProps,
      AttachmentProps
    } = useWorkRequirementLogic({requestID, navigate,toast, setLoading,serviceRequest});

    const {
      handleOpenBundleDlg,
      handleOpenServiceDlg,
      workRequirementList,    
    } = WorkRequirementsProps;

  //#endregion states
 useEffect(() => {
      
      const fetchSchedule = async () => {
        const data = await GetSchedule({ workReqID: 0, entityID: requestID });
        const schedule = data?.result?.find((sch) => sch.subServiceID === 0);
        if (schedule) {
          setScheduleStatus('Reschedule');
          }else{
            setScheduleStatus('Schedule');
        }
      };
      fetchSchedule();
    }, []);

  useEffect(()=>{
      if(serviceRequest?.requestID > 0){
        setRules(getStatusRules(serviceRequest?.status));
        setStatus(serviceRequest?.status);      
      }    
    },[serviceRequest?.requestID]);
  
    useEffect(()=>{
      if(flagList.isViewed && serviceRequest?.isViewed){
  
        const SaveStatus = async () => {
          const result = await SaveService();
        
          if (result) {
            fetchRequests(requestID, false);
            setStatus(serviceRequest.status);
            setRules(getStatusRules(serviceRequest?.status));
            toast.success("Status changed to "+serviceRequest?.status, {
              position: "top-right",
              autoClose: 3000,
              hideProgressBar: true,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
            });        
          } else {
            toast.error("Status update failed.", {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: false,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
            });
          }
        }
        
        SaveStatus();
  
        //fetchRequests(requestID);
        setFlagList((prev)=>({
          ...prev, "isViewed":false
        }));
      }
    },[flagList.isViewed]);
  
    const handleIsViewed = () => {
      if(!serviceRequest?.isViewed && (serviceRequest?.status === ('Submitted' || 'Not Viewed'))){
        setServiceRequest((prevState) => ({
          ...prevState,
          status: 'Under Review',
          isViewed : true
        }));
        setFlagList((prev)=>({
          ...prev, "isViewed":true
        }));
        setStatus('Under Review');   
      }
    };

  const handleStatusChange = (key,value) => {    
      if (canTransition(status, value)) {
        setServiceRequest((prevState) => ({
          ...prevState,
          [key]: value,
        }));
      } else {
        toast.error(`You cannot move from ${status} to ${value}.`, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
      }       
      
    }


  useEffect(() => {  
    if(requestID){
      fetchRequests(requestID, true);
      //fetchWorkRequirements(requestID);
    }      
  }, [requestID]);

  useEffect(()=>{
    if(flagList.isCustomerLoaded && flagList.isJobsiteLoaded, flagList.isContactsLoaded){ 
      const timer = setTimeout(() => {        
        setLoading(false); 
        handleIsViewed();
      }, 1000);
      return () => clearTimeout(timer);                
    }
  },[flagList.isCustomerLoaded && flagList.isJobsiteLoaded, flagList.isContactsLoaded])

  useEffect(() => {    
    fetchJobsites2(serviceRequest?.customerID); 
  }, [serviceRequest?.customerID]);

  const fetchJobsites2 = async (customerID) => {
    if(customerID) {
      try {
        let jobs = await GetJobsite(customerID, 0, navigate);  
        setAllJobsites(jobs);

      } catch (error) {
        console.error("Error fetching Jobsite data:", error);
      }
    }
  };

  useEffect(() => {
    if (serviceRequest?.customerID) {      
      fetchContacts(serviceRequest?.customerID);
      setFlagList((prev)=>({
        ...prev, "isContactsLoaded":true
      })); 
    }    
  }, [serviceRequest?.customerID]);

  useEffect(()=>{
    if(serviceRequest?.jobSiteID  || serviceRequest?.jobSiteID){
      // console.log('Fetching propertyarea of jobsiteID : ', serviceRequest?.jobSiteID)
      setJobsite(allJobsites.filter(x => x.jobsiteID === serviceRequest?.jobSiteID)[0]);
      fetchPropertyArea(serviceRequest?.jobSiteID);
      setFlagList((prev)=>({
        ...prev, "isJobsiteLoaded":true
      })); 
    }
  },[allJobsites]);

  useEffect(()=> {
    setCustomer(allCustomers.filter(x => x.customerID === serviceRequest?.customerID)[0]); 
    setIsCompleted(true);  
    //fetchWorkRequirements(requestID);     
    setFlagList((prev)=>({
      ...prev, "isCustomerLoaded":true
    }));     
  },[allCustomers,serviceRequest?.customerID])

  const fetchRequests = async (RequestID = requestID, loading = true) => {    
    try {
      setLoading(loading);      
      let RequestRaw = await GetServiceRequestRaw(0, RequestID);
      const newRequest = RequestRaw.result[0];
      setServiceRequest(newRequest);
      setOldServiceRequest(newRequest);

       if (!undoClicked) {
          setInitialServiceRequest(newRequest);
        }
    } catch (error) {
      console.error("Error fetching Requests data:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (requestID) {
      fetchLinks();
    }
  }, [requestID]);

  const fetchLinks = async () => {
    setLoading(true);
    try {
      debugger;
      const linkedEntities = await GetEntityLinks(requestID);      
      if (linkedEntities && linkedEntities.length > 0) {
        const linkedEntity = linkedEntities.find((entity => entity.toEntityId
 || entity.toEntityId
 == requestID));
        if (linkedEntity) {
          setLinkedFromEntityId(linkedEntity.fromEntityId);
        }}
    } catch(error){
      console.log(error);
    } finally {
      setLoading(false);
    }
  }
  const validateBasicFields = () => {
    const tempErrors = {};

    if (!serviceRequest.customerID) 
        tempErrors.customerID = 'Customer is required';

    if (!serviceRequest.jobSiteID || !allJobsites?.some((jobsite) => jobsite.jobsiteID === serviceRequest?.jobSiteID)) 
        tempErrors.jobSiteID = 'Jobsite is required';

    if (!serviceRequest.jobsiteContactID || !contacts?.some((contact) => contact.id === serviceRequest?.jobsiteContactID)) 
        tempErrors.jobsiteContactID = 'Jobsite Contact is required';

    if (!serviceRequest.requestTitle) 
        tempErrors.requestTitle = 'Assessment Title is required';

    setRequestErrors(tempErrors);

    return Object.keys(tempErrors).length === 0;
};
 const handleUndo = async () => {
    const newValue = oldServiceRequest;
    setServiceRequest(newValue);

    const result = await SaveService(newValue);
    if(!result.isError){
      fetchRequests();
    }
  };

  const handleSaveServiceRequest = async () => {
    if (!validateBasicFields()) {
      return;
    }    
    setButtonLoading(true);    
    try {
      const result = await SaveService();
      
      if (result) {
        SendNotification(navigate);
        fetchRequests(requestID, true);
        setStatus(serviceRequest.status);
        setRules(getStatusRules(serviceRequest?.status));
         if (!undoClicked) {
                  toast.success(
                    ({}) => (
                      <Box
                                   display="flex"
                                   alignItems="center"
                                   justifyContent="space-between"
                                 >
                                   <Typography sx={{ flex: 1 }}>
                                     Assessment updated successfully.
                                   </Typography>
                                   <Button
                                     variant="contained"
                                     color="main"
                                     size="small"
                                     sx={{
                                       mr: 3,
                                       borderRadius: "20px", 
                                       minWidth: "64px", 
                                     }}
                                     onClick={(e) => {
                                       e.preventDefault();
                                       handleUndo();
                                     }}
                                   >
                                     Undo
                                   </Button>
                                 </Box>
                    ),
         {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });  
      }      
      } else {
        toast.error("Assessment update failed.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
      }
      
    } catch (e) {
      console.error(e);
    } finally {
      setButtonLoading(false);          
    }
  };

  const handleTimeChange = (timeValue, isSelected) => {
    try {
      const timeKey = Number(Object.keys(timeMapping).find((key) => timeMapping[key] === timeValue));
      let updatedTimes = JSON.parse(serviceRequest.availabilityTime || "[]");
  
      if (isSelected) {
        if (timeKey === 1) {
          // If "Anytime" is selected, replace all times with [1, 2, 3, 4]
          updatedTimes = [1, 2, 3, 4];
        } else {
          // Add the specific time
          updatedTimes = [...new Set([...updatedTimes, timeKey])];
          // Remove "Anytime" if another time is selected
          updatedTimes = updatedTimes.filter((t) => t !== 1);
  
          // Automatically check "Anytime" if all 2, 3, 4 are selected
          if (updatedTimes.includes(2) && updatedTimes.includes(3) && updatedTimes.includes(4)) {
            updatedTimes = [1, 2, 3, 4];
          }
        }
      } else {
        // Remove the time from the list
        updatedTimes = updatedTimes.filter((t) => t !== timeKey);
  
        // If "Anytime" is being deselected, clear all times
        if (timeKey === 1) {
          updatedTimes = [];
        } else {
          // Uncheck "Anytime" if any specific time is deselected
          updatedTimes = updatedTimes.filter((t) => t !== 1);
        }
      }
  
      // Update the serviceRequest state
      setServiceRequest((prevState) => ({
        ...prevState,
        availabilityTime: JSON.stringify(updatedTimes),
      }));
        
    } catch (e) {
      console.log("Error in handleTimeChange:", e);
    }
  };
  const handleSubTabChange = (event, newValue) => {
  setActiveSubTab(newValue);
};
  
  const handleDayChange = (day) => {
    // Parse the availabilityDays string into an actual array of numbers
    let updatedDays = serviceRequest.availabilityDays
      ? parseAvailabilityDays(serviceRequest.availabilityDays)
      : [];

    // Get the numeric value of the selected day (e.g., "Mon" -> 1)
    const dayNumber = dayToNumberMap[day];

    // Toggle the day in the list
    if (updatedDays.includes(dayNumber)) {
      // If the day is already selected, remove it
      updatedDays = updatedDays.filter((d) => d !== dayNumber);
    } else {
      // If the day is not selected, add it
      updatedDays.push(dayNumber);
    }

    // Update the serviceRequest state, storing only numbers
    setServiceRequest((prevState) => ({
      ...prevState,
      availabilityDays: JSON.stringify(updatedDays), // Store as a clean stringified array of numbers
    }));
  };

  const dayToNumberMap = {
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
    Sun: 0,
  };

  const daysMapping = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const timeMapping = {
    1: "Anytime",
    2: "Morning",
    3: "Afternoon",
    4: "Evening",
  };

  const allDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const allTimes = ["Anytime", "Morning", "Afternoon", "Evening"];

  const columnsComLog = [
    {
      flex: 0.1,
      field: "communicationType",
      minWidth: 120,
      headerName: "Type",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Icon
            icon={
              row.communicationType === "Email"
                ? "tabler:mail"
                : row.communicationType === "Call"
                ? "tabler:phone-call"
                : row.communicationType === "Text Message"
                ? "tabler:message-circle"
                : row.communicationType === "Mail/Physical Mail"
                ? "tabler:mailbox"
                : ""
            }
          />
          <Typography noWrap sx={{ ml: 2 }}>
            {row.communicationType === "Mail/Physical Mail"
              ? "Letter"
              : row.communicationType === "Text Message"
              ? "SMS"
              : row.communicationType}
          </Typography>
        </Box>
      ),
    },
    {
      flex: 0.25,
      sortable: true,
      field: "description",
      minWidth: 280,
      headerName: "Description",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography noWrap>{row.description}</Typography>
        </Box>
      ),
    },
    // {
    //   flex: 0.1,
    //   field: "date",
    //   minWidth: 210,
    //   headerName: "Log Date",
    //   renderCell: ({ row }) => (
    //     <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
    //       <Typography noWrap>{row.date}</Typography>
    //     </Box>
    //   ),
    // },
    {
      flex: 0.1,
      field: "date",
      minWidth: 220,
      headerName: "Log Date",
      renderCell: ({ row }) => {
        const formattedDate = format(
          new Date(row.date),
          "MM/dd/yyyy, hh:mm:ss a"
        );
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography noWrap>{formattedDate}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      sortable: true,
      field: "record",
      minWidth: 140,
      headerName: "Record Type",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <CustomChip label={row.record} skin="light" color="primary" />
        </Box>
      ),
    },
    {
      flex: 0.1,
      sortable: true,
      field: "recordID",
      minWidth: 140,
      headerName: "Record ID",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography noWrap>
            {row.record === "Request"
              ? `REQ-${row.recordid.toString().padStart(3, "0")}`
              : row.recordid}
          </Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      sortable: true,
      field: "followUp",
      minWidth: 220,
      headerName: "Follow-up",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography noWrap>{row.followUp}</Typography>
        </Box>
      ),
    },
    // {
    //   flex: 0.1,
    //   sortable: true,
    //   field: "createdBy",
    //   minWidth: 50,
    //   headerName: "Created By",
    //   renderCell: ({ row }) => (
    //     <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
    //       <Typography noWrap>{row.createdBy}</Typography>
    //     </Box>
    //   ),
    // },
    {
      flex: 0.1,
      sortable: false,
      field: "actions",
      minWidth: 120,
      headerName: "Actions",
      renderCell: ({ row }) => (
        <Box>
          <IconButton>
            <Icon icon="tabler:pencil" />
          </IconButton>
          <IconButton color="error">
            <Icon icon="tabler:trash" />
          </IconButton>
        </Box>
      ),
    },
  ];


  const handleTabChange = (event, newValue) => {
    setValue(newValue);
  };

  function formatDateSubtractDays(date, days) {
    // Create a copy of the date object to avoid mutating the original one
    const resultDate = new Date(date);

    // Subtract the specified number of days
    resultDate.setDate(resultDate.getDate() - days);

    // Extract year, month, and day from the resultDate
    const year = resultDate.getFullYear();
    const month = (resultDate.getMonth() + 1).toString().padStart(2, "0"); // Months are zero-indexed
    const day = resultDate.getDate().toString().padStart(2, "0");

    // Return the date in yyyy-mm-dd format
    return `${year}-${month}-${day}`;
  }

  const handleDateRange = (startDate, endDate) => {
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const handlePageBack = () => {
    handleNavigation("/assessments");
  };

  useEffect(() => {
    if (serviceRequest?.status) {
      const convertedStatuses = [
        'Assessment',
        'Estimate', 
        'Work Order',
        'Converted to Assessment',
        'Converted to Estimate',
        'Converted to Work Order'
      ];
      
      if (convertedStatuses.includes(serviceRequest.status)) {
        setConversionBannerVisible(true);
      }
      if (linkedFromEntityId) {
        console.log('Request is linked from entity:', linkedFromEntityId);
      }
    }
  }, [serviceRequest?.status, linkedFromEntityId]);
  const getEntityInfo = (status, entityId) => {
    let entityType = '';
    let prefix = '';
    let route = '';
    
    switch(status) {
      case 'Assessment':
      case 'Converted to Assessment':
        entityType = 'Assessment';
        prefix = 'AST';
        route = `/assessments/detail/${entityId}`;
        break;
      case 'Estimate':
      case 'Converted to Estimate':
        entityType = 'Estimate';
        prefix = 'EST';
        route = `/estimates/detail/${entityId}`;
        break;
      case 'Work Order':
      case 'Converted to Work Order':
        entityType = 'WorkOrder';
        prefix = 'WO';
        route = `/work-orders/detail/${entityId}`;
        break;
      case 'Request':
      default:
        entityType = 'Request';
        prefix = 'REQ';
        route = `/requests/detail/${entityId}`;
        break;
    }
    
    return { entityType, prefix, route };
  };
  const getInitials = (name) => {
    if (!name) return "";
    const nameParts = name.split(" ");
    return nameParts
      .map((part) => part[0]?.toUpperCase())
      .join("")
      .slice(0, 2);
  };

  const [assignees, setAssignees] = useState([]);
  const [mountAssigneeTab, setMountAssigneeTab] = useState(false);

  useEffect(() => {
    if (!mountAssigneeTab) {
      const timeout = setTimeout(() => {
        setMountAssigneeTab(true);
      }, 5000);
  
      return () => clearTimeout(timeout);
    }
  }, [mountAssigneeTab]);

  const [openInvoiceDlg, setOpenInvoiceDlg] = useState(false);

  const handleOpenInvoiceDlg = () => setOpenInvoiceDlg(true);
  const handleCloseInvoiceDlg = () => setOpenInvoiceDlg(false);
  // Property Area Dialog/Form Component
const PropertyAreaForm = ({ open, onClose, onSave }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add Property Area</DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Property Area Name"
              placeholder="Enter property area name"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              placeholder="Enter description"
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2 }}>Property Notes</Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Enter note..."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={onSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

  return (
    <UserLayout
      contentHeightFixed={contentHeightFixed}
      activeLink={"Assessments"}
      showIcon={true}
      NavURL={handlePageBack}
    >
      <Card sx={{ width: "100%", height: "100%", position: "relative" }}>
        <CardHeader
          title={
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", md: "center" },
                gap: { xs: 2, md: 0 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  alignItems: { xs: "flex-start", md: "center" },
                  gap: { xs: 1, md: 2 },
                  width: { xs: "100%", md: "auto" },
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: "bold" }}>
                  AST-{serviceRequest?.requestID}
                </Typography>
                <HtmlTooltip
                  title={
                    <Box sx={{ p: (4, 2) }}>
                      <Box sx={{ mb: 6 }}>
                        <Typography
                          color="text.primary"
                          fontSize="small"
                          fontWeight="600"
                          mb={2}
                        >
                          Customer Details
                        </Typography>
                        <Box sx={{ display: "flex", gap: 20 }}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            {customer?.customerType === "Business" ? (
                              <Typography
                                color="text.secondary"
                                fontSize="small"
                              >
                                Company Name
                              </Typography>
                            ) : (
                              <Typography
                                color="text.secondary"
                                fontSize="small"
                              >
                                Customer Name
                              </Typography>
                            )}
                            <Typography color="text.secondary" fontSize="small">
                              Email
                            </Typography>
                            <Typography color="text.secondary" fontSize="small">
                              Phone
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            {customer?.customerType === "Business" ? (
                              <Typography fontSize="small">
                                {customer?.companyName}
                              </Typography>
                            ) : (
                              <Typography fontSize="small">
                                {serviceRequest?.customerName}
                              </Typography>
                            )}
                            <Typography fontSize="small">
                              {customer?.email}
                            </Typography>
                            <Typography fontSize="small">
                              {customer?.phoneNumbers[0].phoneNo}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  }
                >
                  <Box>
                    <CustomChip
                      sx={{ cursor: "pointer" }}
                      label={
                        <Typography
                          variant="body1"
                          color="primary"
                          sx={{ maxWidth: "200px", overflow: "hidden" }}
                        >
                          {serviceRequest?.requestTitle}
                        </Typography>
                      }
                      color="secondary"
                      skin={theme.palette.mode === "dark" ? "dark" : "light"}
                      icon={<Icon icon="tabler:building" />}
                    />
                  </Box>
                </HtmlTooltip>
                {/* <CustomChip label="Internal" color="info" skin="light" /> */}
               <CustomChipWithDropdown
                tag={serviceRequest?.status}
                onSelect={(selectedTag) => {
                  handleStatusChange('status', selectedTag);
                }}
              />
                {/* <AvatarGroup
                  className="pull-up"
                  max={4}
                  sx={{
                    "& .MuiAvatar-root": { width: 25, height: 25 },
                    "& .MuiAvatarGroup-avatar": { width: 25, height: 25 },
                  }}
                >
                  {assignees.map((employee, index) => {
                      const employeeFullName = `${employee.firstName} ${employee.lastName}`;
                      return (
                        <Tooltip key={employee.employeeID} title={employeeFullName}>
                          <Avatar
                            src={employee.avatarUrl || `/images/avatars/${index + 1}.png`}
                            alt={employeeFullName}
                            sx={{ width: 25, height: 25 }}
                          />
                        </Tooltip>
                      );
                    })}
                </AvatarGroup> */}
                <AvatarGroup
                  className="pull-up"
                  max={4}
                  sx={{
                    "& .MuiAvatar-root": { width: 25, height: 25 },
                    "& .MuiAvatarGroup-avatar": { width: 25, height: 25 },
                  }}
                >
                  {filteredEmployees &&
                    filteredEmployees
                      .filter((employee) =>
                        assignedUserIds.includes(employee.employeeID)
                      )
                      .map((employee, index) => {
                        const employeeFullName = `${employee.firstName} ${employee.lastName}`;
                        return (
                          <Tooltip
                            key={employee.employeeID}
                            title={employeeFullName}
                          >
                            <Avatar
                              src={
                                employee.avatarUrl ||
                                `/images/avatars/${index + 1}.png`
                              }
                              alt={employeeFullName}
                              sx={{ width: 25, height: 25 }}
                            />
                          </Tooltip>
                        );
                      })}
                </AvatarGroup>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                {/* <Button variant='outlined' color='main' onClick={handleOpenAssignToDlg}>Assign To</Button> */}
                <Button
                  variant="contained"
                  color="main"
                  {...(hasAssessmentSavePermission && {
                    onClick: () => handleSaveServiceRequest(),
                  })}
                  disabled={
                    btnLoading || !isChanged || !hasAssessmentSavePermission
                  }
                >
                  {btnLoading ? <CircularProgress size={24} /> : "Save Changes"}
                </Button>
                <Button
                  variant="outlined"
                  color="main"
                  startIcon={<Icon icon="tabler:calendar" />}
                  disabled={!rules.canEdit || !hasAssessmentSavePermission}
                  {...(hasAssessmentSavePermission && {
                    onClick: () => handleOpenScheduleAstDlg(),
                  })}
                >
                  {scheduleStatus}
                </Button>
                <ConversionComponent
                  handleOpenSchedule={handleOpenScheduleAstDlg}
                  schedulingReq={schedulingReq}
                  setSchedulingReq={setSchedulingReq}
                  originalRequestID={requestID}
                  openScheduleAst={openScheduleAst}
                  handleCloseScheduleAstDlg={handleCloseScheduleAstDlg}
                  setLoading={setLoading}
                  module="Assessment"
                  rules={rules}
                  hasConvertToPermission={true}
                />
                {scheduleStatus !== "Schedule" && (
                  <OptionsButton generateInvoice={handleOpenInvoiceDlg} />
                )}
              </Box>
            </Box>
          }
        />
         {conversionBannerVisible && serviceRequest?.status && (
        <Box
          sx={{
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(251,188,4,0.15)' : 'rgb(255, 224, 100)',
            border: `1px solid ${theme.palette.mode === 'dark' ? '#b38f00' : 'rgb(255, 224, 100)'}`,
            borderRadius: 1,
            padding: 4,
            margin: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <Icon icon="tabler:info-circle" color={theme.palette.mode === 'dark' ? 'rgb(255, 224, 100)' : theme.palette.primary.main} />
          <Typography
            variant="h6"
            sx={{ color: theme.palette.mode === 'dark' ? theme.palette.warning.light : theme.palette.primary.main }}
          >
            This has been converted into {serviceRequest.status === 'Work Order' ? 'a' : 'an'} <strong>{serviceRequest.status}</strong>, and you can no longer make changes.
            {linkedFromEntityId && (
              <span>
                {' '}Click here to view{' '}
                <Link
                  to={getEntityInfo(serviceRequest.status, linkedFromEntityId).route}
                  style={{
                    color: theme.palette.mode === 'dark' ? theme.palette.warning.light : theme.palette.primary.main,
                    textDecoration: 'underline',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(getEntityInfo(serviceRequest.status, linkedFromEntityId).route);
                  }}
                >
                  {getEntityInfo(serviceRequest.status, linkedFromEntityId).prefix}-{linkedFromEntityId}
                </Link>
              </span>
            )}
          </Typography>
        </Box>
      )}
        <CardContent>
          <Box>
            <TabContext value={value}>
              <TabList
                sx={{ mb: 2 }}
                onChange={handleTabChange}
                scrollButtons="auto"
                variant="scrollable"
                aria-label="simple tabs example"
              >
                <Tab
                  value="1"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                        color:
                          theme.palette.mode === "dark"
                            ? value === "1"
                              ? "#fff"
                              : "inherit"
                            : "inherit",
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: { xs: "0.875rem", md: "1rem" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Assessment Details
                      </Box>
                    </Box>
                  }
                />
                <Tab
                  value="2"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                        color:
                          theme.palette.mode === "dark"
                            ? value === "2"
                              ? "#fff"
                              : "inherit"
                            : "inherit",
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: { xs: "0.875rem", md: "1rem" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Assessment Form
                      </Box>
                    </Box>
                  }
                />
                <Tab
                  value="3"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                        color:
                          theme.palette.mode === "dark"
                            ? value === "3"
                              ? "#fff"
                              : "inherit"
                            : "inherit",
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: { xs: "0.875rem", md: "1rem" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Attachments
                      </Box>
                    </Box>
                  }
                />
                <Tab
                  value="4"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                        color:
                          theme.palette.mode === "dark"
                            ? value === "4"
                              ? "#fff"
                              : "inherit"
                            : "inherit",
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: { xs: "0.875rem", md: "1rem" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Notes
                      </Box>
                    </Box>
                  }
                />
                <Tab
                  value="6"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                        color:
                          theme.palette.mode === "dark"
                            ? value === "6"
                              ? "#fff"
                              : "inherit"
                            : "inherit",
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: { xs: "0.875rem", md: "1rem" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Linked Records
                      </Box>
                    </Box>
                  }
                />
                <Tab
                  value="7"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                        color:
                          theme.palette.mode === "dark"
                            ? value === "7"
                              ? "#fff"
                              : "inherit"
                            : "inherit",
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: { xs: "0.875rem", md: "1rem" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Assignees
                      </Box>
                    </Box>
                  }
                />
              </TabList>
              <TabPanel value="1" sx={{ px: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    gap: { md: 8, xs: 0 },
                    flexDirection: { md: "row", xs: "column" },
                  }}
                >
                  {/* Left Column */}
                  <Box
                    sx={{
                      width: { md: "30%", xs: "100%" },
                      maxWidth: { md: "30%", xs: "100%" },
                    }}
                  >
                    <Grid container spacing={5}>
                      <Grid item xs={12}>
                        <Divider
                          sx={{
                            color: "text.disabled",
                            "& .MuiDivider-wrapper": { px: 6 },
                            fontSize: theme.typography.body2.fontSize,
                            my: (theme) => `${theme.spacing(3)} !important`,
                          }}
                        >
                          General Information
                        </Divider>
                      </Grid>
                      <Grid item xs={12}>
                        <CustomTextField
                          fullWidth
                          label="Assessment Title"
                          required
                          disabled={!rules.canEdit}
                          placeholder="Enter a brief assessment title"
                          error={!!requestErrors.requestTitle}
                          helperText={requestErrors.requestTitle}
                          onChange={(e) => {
                            setServiceRequest((prevState) => ({
                              ...prevState,
                              requestTitle: e.target.value,
                            }));
                          }}
                          value={serviceRequest?.requestTitle}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <CustomTextField
                          fullWidth
                          label="Description"
                          multiline
                          rows={2}
                          disabled={!rules.canEdit}
                          placeholder="Add a description..."
                          onChange={(e) => {
                            setServiceRequest((prevState) => ({
                              ...prevState,
                              reqDescription: e.target.value,
                            }));
                          }}
                          value={serviceRequest?.reqDescription}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormLabel
                          sx={{
                            fontSize: "13px",
                            color: "text.primary",
                            mb: "4px",
                          }}
                        >
                          Customer Name{" "}
                          <span style={{ color: theme.palette.error.main }}>
                            *
                          </span>
                        </FormLabel>
                        <Box
                          sx={{
                            border: `1px solid rgba(47, 43, 61, 0.2)`,
                            p: 2,
                            borderRadius: 1,
                            cursor: "default",
                            backgroundColor: "action.disabledBackground",
                          }}
                        >
                          <Typography variant="body1">
                            {customer?.customerType === "Individual"
                              ? `${customer?.firstName} ${customer?.lastName}`
                              : customer?.customerType === "Business"
                              ? customer?.companyName
                              : "N/A"}
                          </Typography>
                        </Box>
                      </Grid>
                  <Grid item xs={12}>
                    <CustomAutocomplete
                      fullWidth
                      required
                      disabled={!customer?.customerID}
                      options={[
                        { jobsiteID: "create-new", jobsiteName: "+ Create New Jobsite", isCreateOption: true },
                        ...((allJobsites || [])
                          .filter(option => option.jobsiteName)
                          .sort((a, b) => a.jobsiteName.localeCompare(b.jobsiteName)))
                      ]}
                      getOptionLabel={(option) => option.jobsiteName || ""}
                      value={
                        allJobsites?.find(
                          (jobsite) => jobsite.jobsiteID === serviceRequest?.jobSiteID
                        ) || null
                      }
                      onChange={(event, newValue) => {
                        if (newValue?.isCreateOption) {
                          if (customer?.customerID) {
                            handleOpenNewJobsiteDlg();
                          }
                        } else if (newValue) {
                          handleJobsiteDropdownChange(newValue.jobsiteID);
                        }
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option.jobsiteID === value.jobsiteID
                      }
                      filterOptions={(options, params) => {
                        const filtered = options
                          .filter((option) => {
                            if (option.isCreateOption) return true;
                            const jobsiteName = option.jobsiteName || "";
                            return jobsiteName.toLowerCase().includes(params.inputValue.toLowerCase());
                          })
                          .filter(option => option.isCreateOption || option.jobsiteName);
                        return filtered;
                      }}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <span style={{ 
                            fontWeight: option.isCreateOption ? 'bold' : 'normal',
                            color: option.isCreateOption && !customer?.customerID ? 'rgba(0, 0, 0, 0.38)' : 'inherit',
                          }}>
                            {option.jobsiteName}
                          </span>
                        </li>
                      )}
                      renderInput={(params) => (
                        <CustomTextField
                          {...params}
                          label="Jobsite"
                          required
                          placeholder="Select Jobsite"
                          error={!!requestErrors.jobSiteID}
                          helperText={requestErrors.jobSiteID}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <CustomAutocomplete
                      fullWidth
                      required
                      disabled={!rules.canEdit}
                      id="jobsite-contact-autocomplete"
                      options={[
                        { id: "create-new", contact: "+ Create New Contact", isCreateOption: true },
                        ...((contacts || []).sort((a, b) => (a.contact || "").localeCompare(b.contact || "")))
                      ]}
                      getOptionLabel={(option) => option.contact || ""}
                      value={
                        contacts?.find(
                          (contact) => contact.id === serviceRequest?.jobsiteContactID
                        ) || null
                      }
                      onChange={(event, newValue) => {
                        if (newValue?.isCreateOption) {
                          handleOpenNewContactDlg();
                        } else if (newValue) {
                          handleContactDropdownChange(newValue.id);
                        }
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      filterOptions={(options, params) => {
                        return options.filter((option) => {
                          if (option.isCreateOption) return true;
                          return (option.contact || "").toLowerCase().includes(params.inputValue.toLowerCase());
                        });
                      }}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <span style={{ 
                            fontWeight: option.isCreateOption ? 'bold' : 'normal',
                          }}>
                            {option.contact}
                          </span>
                        </li>
                      )}
                      renderInput={(params) => (
                        <CustomTextField
                          {...params}
                          label="Jobsite Contact"
                          required
                          placeholder="Select Jobsite Contact"
                          error={!!requestErrors.jobsiteContactID}
                          helperText={requestErrors.jobsiteContactID}
                        />
                      )}
                    />
                  </Grid>
                    </Grid>
                  </Box>
                  <Divider orientation="vertical" flexItem />
                  <Box sx={{ maxWidth: "70%" }}>
                    <Grid container spacing={5}>
                      <Grid item xs={12}>
                        <Divider
                          sx={{
                            color: "text.disabled",
                            "& .MuiDivider-wrapper": { px: 6 },
                            fontSize: theme.typography.body2.fontSize,
                            my: (theme) => `${theme.spacing(3)} !important`,
                          }}
                        >
                          Availability Details
                        </Divider>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <FormLabel
                            sx={{ fontSize: "13px", color: "text.primary" }}
                          >
                            Preferred Times
                          </FormLabel>
                          <FormControl component="fieldset">
                            {allTimes.map((time, index) => {
                              // Check if "Anytime" is checked
                              const isAnytimeChecked =
                                serviceRequest?.availabilityTime.includes("1");

                              // Determine if the current time should be checked
                              const isChecked =
                                isAnytimeChecked ||
                                serviceRequest?.availabilityTime.includes(
                                  Number(
                                    Object.keys(timeMapping).find(
                                      (key) => timeMapping[key] === time
                                    )
                                  )
                                );
                              return (
                                <FormControlLabel
                                  key={index}
                                  control={
                                    <Checkbox
                                      disabled={!rules.canEdit}
                                      checked={isChecked}
                                      onChange={(e) =>
                                        handleTimeChange(time, e.target.checked)
                                      }
                                      color="success"
                                    />
                                  }
                                  label={time}
                                />
                              );
                            })}
                          </FormControl>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <FormLabel
                            sx={{
                              fontSize: "13px",
                              color: "text.primary",
                              marginBottom: "4px",
                            }}
                          >
                            Preferred Days
                          </FormLabel>
                          <ToggleButtonGroup value={availabilityDaysArray}>
                            {allDays.map((day, index) => {
                              const dayNumber = dayToNumberMap[day];
                              return (
                                <ToggleButton
                                  // disabled={
                                  //   !rules?.canEdit ||
                                  //   dayNumber === 0 ||
                                  //   dayNumber === 6
                                  // }
                                  key={index}
                                  value={day}
                                  color={"info"}
                                  selected={availabilityDaysArray.includes(
                                    dayNumber
                                  )} // Mark selected ones
                                  onChange={() => handleDayChange(day)}
                                  sx={{
                                    fontSize: { xs: "11px", sm: "13px" },
                                    px: { xs: 1, sm: 2 },
                                    minWidth: { xs: "40px", sm: "60px" },
                                  }}
                                >
                                  {day}
                                </ToggleButton>
                              );
                            })}
                          </ToggleButtonGroup>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormLabel
                          sx={{
                            fontWeight: 400,
                            fontSize: "13px",
                            marginBottom: "4px",
                            color: "text.primary",
                            height: "100%",
                          }}
                        >
                          Complete Work By
                        </FormLabel>
                        {loading ? (
                          <p></p> // Show a loading indicator while data is being fetched
                        ) : (
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              disabled={!rules.canEdit}
                              sx={{ width: "100%" }}
                              value={
                                serviceRequest?.completeWorkBy &&
                                serviceRequest?.completeWorkBy !==
                                  "0001-01-01T00:00:00" &&
                                serviceRequest?.completeWorkBy !==
                                  "1900-01-01T00:00:00"
                                  ? dayjs(serviceRequest.completeWorkBy)
                                  : null
                              }
                              onChange={(newValue) => {
                                handleCompleteWorkByDateChange(newValue);
                              }}
                              slotProps={{ textField: { size: "small" } }}
                            />
                          </LocalizationProvider>
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <Divider
                          sx={{
                            color: "text.disabled",
                            "& .MuiDivider-wrapper": { px: 6 },
                            fontSize: theme.typography.body2.fontSize,
                            my: (theme) => `${theme.spacing(6)} !important`,
                          }}
                        >
                          Additional Details
                        </Divider>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          <Typography
                            variant="body1"
                            sx={{ mt: 2, mr: 6, mb: 4 }}
                          >
                            Tags
                          </Typography>
                          <IconButton
                            onClick={() => {
                              handleOpenTagManagementDlg();
                            }}
                            // disabled={!rules.canEdit}
                            sx={{ mb: 4 }}
                          >
                            <AddIcon />
                          </IconButton>
                          {serviceRequest?.tags &&
                          serviceRequest.tags.length > 0 ? (
                            serviceRequest.tags.map((tag) => (
                              <CustomChip
                                label={tag.tagName}
                                skin="light"
                                color="info"
                                // disabled={!rules.canEdit}
                                deleteIcon={
                                  <Tooltip title="Click to remove tag">
                                    <CloseIcon />
                                  </Tooltip>
                                }
                                onDelete={() => handleDeleteTag(tag.tagName)}
                                sx={{
                                  cursor: "pointer",
                                  mr: 2,
                                  mb: 2,
                                  mt: 2,
                                }}
                              />
                            ))
                          ) : (
                            <Typography variant="body2" sx={{ ml: 2, mt: 2 }}>
                              No tags
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          <Button
                            sx={{ position: "absolute", bottom: 20, right: 20 }}
                            variant="contained"
                            color="main"
                            {...(hasAssessmentSavePermission && {
                              onClick: () => handleSaveServiceRequest(),
                            })}
                            disabled={
                              btnLoading ||
                              !isChanged ||
                              !hasAssessmentSavePermission
                            }
                          >
                            {btnLoading ? (
                              <CircularProgress size={24} />
                            ) : (
                              "Save Changes"
                            )}
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </TabPanel>
              <TabPanel value="2" sx={{ px: 0 }}>
                <Tabs value={activeSubTab} onChange={handleSubTabChange} sx={{ mb: 5 }}>
                  <Tab label="Property Notes" />
                  <Tab label="Service & Bundles" />
                </Tabs>
                {activeSubTab === 0 ? (
                  <>
                  {(workRequirementList && workRequirementList.length > 0) ? (
                      (() => {
                        const schedulingWR = workRequirementList?.filter((wr) => wr.subServiceID === 0);
                        const workReqID = schedulingWR?.[0]?.workReqID;
                        const jobID = schedulingWR?.[0]?.jobs?.[0]?.jobID;

                        return (
                          <PropertyAreaTab rules={rules} theme={theme} jobID={jobID} workReqID={workReqID} />
                        );
                      })()
                    ) : (
                      <Box sx={{ position: "relative", width: "100%" }}>
                        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                          <Button
                            variant="contained"
                            color="main"
                            startIcon={<AddIcon />}
                            disabled={!isScheduled}   // 👈 enable if scheduled
                          >
                            Add Property Notes
                          </Button>
                        </Box>

                        {!isScheduled && (  // 👈 show message only if not scheduled
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              mx: "auto",
                              border: "1px solid #EAEAEA",
                              borderRadius: 1,
                              width: "100%",
                              height: 280,
                              py: 4,
                              alignItems: "center",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                                justifyContent: "center",
                                alignItems: "center",
                                width: { xs: "90%", sm: "100%" },
                                maxWidth: 400,
                                px: 2,
                                textAlign: "center"
                              }}
                            >
                              <img
                                src={AddEmployeeDoodle}
                                alt="Schedule Assessment"
                                style={{ width: "100px", height: "auto" }}
                              />
                              <Typography variant="h6">
                                Schedule assessment to add property notes
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )}

                  </>
                ) : (
                  <Grid container spacing={5}>
                {workRequirementList && workRequirementList.length === 0 ? (
                  <>
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent
                          sx={{
                            display: "flex",
                            textAlign: "center",
                            alignItems: "center",
                            flexDirection: "column",
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "#1B2431"
                                : "#FBFBFC",
                            p: (theme) =>
                              `${theme.spacing(9.75, 5, 9.25)} !important`,
                          }}
                        >
                          <CustomAvatar
                            skin="light"
                            color="info"
                            sx={{ mb: 2.25 }}
                          >
                            <Icon icon="tabler:building" />
                          </CustomAvatar>
                          <Typography variant="h5" sx={{ mb: 2.75 }}>
                            No Bundles added yet
                          </Typography>
                          <Typography sx={{ mb: 6, color: "text.secondary" }}>
                            Add bundles that are required by clicking on "Add Bundle" button
                          </Typography>
                          <Button
                            variant="outlined"
                            color={
                              theme.palette.mode === "dark"
                                ? "secondary"
                                : "primary"
                            }
                            startIcon={<AddIcon color="info" />}
                            onClick={handleOpenBundleDlg}
                            disabled={!rules.canEdit}
                          >
                            Add Bundle
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent
                          sx={{
                            display: "flex",
                            textAlign: "center",
                            alignItems: "center",
                            flexDirection: "column",
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "#1B2431"
                                : "#FBFBFC",
                            p: (theme) =>
                              `${theme.spacing(9.75, 5, 9.25)} !important`,
                          }}
                        >
                          <CustomAvatar
                            skin="light"
                            color="info"
                            sx={{ mb: 2.25 }}
                          >
                            <Icon icon="tabler:building" />
                          </CustomAvatar>
                          <Typography variant="h5" sx={{ mb: 2.75 }}>
                            No Services added yet
                          </Typography>
                          <Typography sx={{ mb: 6, color: "text.secondary" }}>
                            Add services that are required by clicking on "Add Service" button
                          </Typography>
                          <Button
                            variant="outlined"
                            color={
                              theme.palette.mode === "dark"
                                ? "secondary"
                                : "primary"
                            }
                            startIcon={<AddIcon color="info" />}
                            onClick={handleOpenServiceDlg}
                            disabled={!rules.canEdit}
                          >
                            Add Service
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  </>
                ) : (
                  <WorkRequirements
                    entity="assessment"
                    {...WorkRequirementsProps}
                    rules={rules}
                  />
                  )}
                  </Grid>
                )}
              </TabPanel>
              <TabPanel value="3" sx={{ px: 0 }}>
                <AttachmentsComponent
                  rules={rules}
                  request={serviceRequest}
                  requestType={"Assessment"}
                  handleOpenUnsavedChangesDlg={handleOpenUnsavedChangesDlg}
                  theme={theme}
                  loading={loading}
                  AttachmentProps={AttachmentProps}
                  openUploadDlg2={openUploadDlg2}
                  setOpenUploadDlg2={setOpenUploadDlg2}
                />
              </TabPanel>
              <TabPanel value="4" sx={{ p: 0, pt: 4 }}>
                <Box
                  sx={{
                    height: "70vh",
                  }}
                >
                  <NotesComponent
                    requestID={requestID}
                    requestType={"Assessment"}
                    customerID={serviceRequest?.customerID}
                    setLoading={setLoading}
                    rules={rules}
                  />
                </Box>
              </TabPanel>
              {/* <TabPanel value="5">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 4,
                  }}
                >
                  <Typography variant="body1">
                    {sortTimeline ? "Newest First" : "Oldest First"}
                  </Typography>
                  <IconButton onClick={() => setSortTimeline(!sortTimeline)}>
                    <Icon
                      icon={`tabler:sort-${
                        sortTimeline ? "descending" : "ascending"
                      }`}
                    />
                  </IconButton>
                </Box>
                <Timeline sx={{ mb: 6 }}>
                  <Typography variant="h5" sx={{ fontWeight: "bold", mb: 4 }}>
                    Today - Jun 03, 2024
                  </Typography>
                  <TimelineItem>
                    <TimelineSeparator>
                      <TimelineDot color="success" />
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent
                      sx={{ "& svg": { verticalAlign: "bottom", mx: 4 } }}
                    >
                      <Box
                        sx={{
                          mb: 2,
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        <Typography sx={{ mr: 6 }}>
                          {<strong>John Smith</strong>} added an{" "}
                          {<strong>Attachment</strong>}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          30 minutes ago
                        </Typography>
                      </Box>
                      <Box
                        sx={{ mt: 2, display: "flex", alignItems: "center" }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          None
                        </Typography>
                        <Icon icon="material-symbols:arrow-right-alt-rounded" />
                        <Typography variant="body2">
                          mylawn_frontyard.png
                        </Typography>
                      </Box>
                    </TimelineContent>
                  </TimelineItem>
                  <TimelineItem>
                    <TimelineSeparator>
                      <TimelineDot color="success" />
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent
                      sx={{ "& svg": { verticalAlign: "bottom", mx: 4 } }}
                    >
                      <Box
                        sx={{
                          mb: 2,
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        <Typography sx={{ mr: 4 }}>
                          {<strong>John Smith</strong>} added a{" "}
                          {<strong>Link</strong>}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          30 minutes ago
                        </Typography>
                      </Box>
                      <Box
                        sx={{ mt: 2, display: "flex", alignItems: "center" }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          None
                        </Typography>
                        <Icon icon="material-symbols:arrow-right-alt-rounded" />
                        <Typography variant="body2">
                          This request is linked with Estimation EST-001
                        </Typography>
                      </Box>
                    </TimelineContent>
                  </TimelineItem>
                </Timeline>
                <Timeline>
                  <Typography variant="h5" sx={{ fontWeight: "bold", mb: 4 }}>
                    Jun 01, 2024
                  </Typography>
                  <TimelineItem>
                    <TimelineSeparator>
                      <TimelineDot color="success" />
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent
                      sx={{ "& svg": { verticalAlign: "bottom", mx: 4 } }}
                    >
                      <Box
                        sx={{
                          mb: 2,
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        <Typography sx={{ mr: 6 }}>
                          {<strong>John Smith</strong>} added a{" "}
                          {<strong>Note</strong>}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          03:05 PM
                        </Typography>
                      </Box>
                      <Box
                        sx={{ mt: 2, display: "flex", alignItems: "center" }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          None
                        </Typography>
                        <Icon icon="material-symbols:arrow-right-alt-rounded" />
                        <Typography variant="body2"></Typography>
                      </Box>
                    </TimelineContent>
                  </TimelineItem>
                  <TimelineItem>
                    <TimelineSeparator>
                      <TimelineDot color="success" />
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent
                      sx={{ "& svg": { verticalAlign: "bottom", mx: 4 } }}
                    >
                      <Box
                        sx={{
                          mb: 2,
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        <Typography sx={{ mr: 4 }}>
                          {<strong>John Smith</strong>} changed the{" "}
                          {<strong>Status</strong>}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          02:20 PM
                        </Typography>
                      </Box>
                      <Box
                        sx={{ mt: 2, display: "flex", alignItems: "center" }}
                      >
                        <CustomChip
                          rounded
                          size="small"
                          skin="light"
                          sx={{ lineHeight: 1 }}
                          color="info" // Use color names: primary, secondary, error, warning, info, success
                          label="IN PROGRESS"
                        />
                        <Icon icon="material-symbols:arrow-right-alt-rounded" />
                        <CustomChip
                          rounded
                          size="small"
                          skin="light"
                          sx={{ lineHeight: 1 }}
                          color="success" // Use color names: primary, secondary, error, warning, info, success
                          label="REVIEWED"
                        />
                      </Box>
                    </TimelineContent>
                  </TimelineItem>
                  <TimelineItem>
                    <TimelineSeparator>
                      <TimelineDot color="success" />
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent
                      sx={{ "& svg": { verticalAlign: "bottom", mx: 4 } }}
                    >
                      <Box
                        sx={{
                          mb: 2,
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        <Typography sx={{ mr: 4 }}>
                          {<strong>John Smith</strong>} created the{" "}
                          {<strong>Request</strong>}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          02:00 PM
                        </Typography>
                      </Box>
                    </TimelineContent>
                  </TimelineItem>
                </Timeline>
              </TabPanel> */}
              <TabPanel value="6" sx={{ px: 0 }}>
                <LinkedRecordsComponent
                  requestID={requestID}
                  customerID={serviceRequest?.customerID}
                  setLoading={setLoading}
                  module="detail"
                  rules={rules}
                />
              </TabPanel>

              <TabPanel value="7" sx={{ px: 0 }}>
                <Grid container spacing={5}>
                  <AssignToComponent
                    requestID={requestID}
                    serviceRequest={serviceRequest}
                    requestType={"Assessment"}
                    setLoading={setLoading}
                    rules={rules}
                    navigate={navigate}
                    handleServiceRequestChange={handleServiceRequestChange}
                  />
                </Grid>
              </TabPanel>
            </TabContext>
          </Box>
        </CardContent>
      </Card>

      {/* Popup Dialogs */}

      <ScheduleAssessment
        open={openScheduleAst}
        handleClose={handleCloseScheduleAstDlg}
        entity="schedule"
        requestID={requestID}
        setSchedulingReq={setSchedulingReq}
        requestType={"Assessment"}
        setLoading={setLoading}
        scheduleStatus={scheduleStatus}
        fetchRequests={fetchRequests}
      />

      <AddCustomerDialog
        open={openNewCustomerDlg}
        setOpenNewCustomerDlg={setOpenNewCustomerDlg}
        fetchData={fetchData}
        allCustomers={allCustomers}
      />

      <AddContactDialog
        open={openNewContactDlg}
        handleClose={handleCloseContactDialog}
        customer={customer}
        setLoading={setLoading}
        fetchContactData={fetchContacts}
        toast={toast}
        contacts={allContacts}
      />
      
       <AddJobsiteDialog
                   open={openNewJobsiteDlg}
                   handleClose={handleCloseNewJobsiteDlg}
                   setJobsiteOpen={setOpenNewJobsiteDlg}
                   customer={customer}
                   fetchContactData={fetchContacts}
                   toast={toast}
                   jobsite={jobsite}
                   handleJobsiteChange={handleJobsiteChange}
                   navigate={navigate}
                   NProgress={NProgress}
                   fetchContacts={fetchContacts}
                   fetchJobsites={fetchJobsites}
                   contacts={allContacts}
                   propertyArea={propertyArea}
                   setPropertyArea={setPropertyArea}
                   areaMeasurement={areaMeasurement}
                   setAreaMeasurement={setAreaMeasurement}
                   setLoading={setLoading}
                   activeStep={activeStepJobsite}
                   setActiveStep={setActiveStepJobsite}
                   clearAreaMeasurement={clearAreaMeasurement}
                   fetchPropertyArea={fetchPropertyArea}
                   propertyAreaList={propertyAreaList}
                   clearPropertyArea={clearPropertyArea}
                   jobsiteContent={jobsiteContent}
                   alertList={alertList}
                   setAlertList={setAlertList}
                   files={files}
                   setFiles={setFiles}
                 />
      <AddBundle {...AddBundleProps} />

      <AddService {...AddServiceProps} />

      <PreAttachment Title="Upload Attachment" {...AttachmentProps} />

      <TagManagementDialog
        open={openTagManagementDlg}
        handleClose={handleCloseTagManagementDlg}
        data={{ title: "Tag Management" }}
        handleAddTag={handleAddTag}
        handleDeleteTag={handleDeleteTag}
        tags={serviceRequest?.tags}
        tag={Tag}
        setTag={setTag}
      />

      <GenerateInvoiceDialog
        open={openInvoiceDlg}
        handleClose={handleCloseInvoiceDlg}
        assessmentID={requestID}
        setLoading={setLoading}
        openScheduleAst={openScheduleAst}
      />

      <UnsavedChangesDialog
        open={openUnsavedChangesDlg}
        handleClose={handleCloseUnsavedChangesDlg}
        handleDiscard={handleDiscardChanges}
      />
      {/* <AddCommunicationLogDialog
        open={openComLog}
        handleClose={handleCloseNewComLogDlg}
      /> */}

      {/* Send Portal Invite Sent Toast */}
      {/*<ToastContainer />*/}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1000 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </UserLayout>
  );
};

export default AssessmentDetail;
